import { useEffect, useState } from 'react';
import { Send, User } from 'lucide-react';
import { useModernChatStore } from '../../../stores/modern-chat-store';
import { Button } from '../../ui/Button';
import BroccoliCharacter from '../../../assets/broccoli_character.png';

function ModernInputArea() {
  const sendChatMessageAsync = useModernChatStore((state) => state.sendChatMessageAsync);
  const pendingResponse = useModernChatStore((state) => state.pendingResponse);
  const initChatWebSocket = useModernChatStore((state) => state.initChatWebSocket);
  const currWebSocket = useModernChatStore((state) => state.currWebSocket);
  const [message, setMessage] = useState('');

  useEffect(() => {
    initChatWebSocket();
    return () => {
      try {
        if (currWebSocket) {
          currWebSocket.close();
        }
      } catch (err) {
        console.log('Error closing WebSocket:', err);
      }
    };
  }, [initChatWebSocket]);

  const handleSendMessage = async () => {
    if (message.trim().length === 0) return;
    setMessage('');
    sendChatMessageAsync(message);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 backdrop-blur-sm bg-white/95">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end gap-3 p-4 bg-gray-50/80 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-gray-300 focus-within:shadow-md">
          <div className="flex-1 min-h-[40px] max-h-32">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={pendingResponse}
              rows={1}
              className="w-full bg-transparent border-none outline-none resize-none text-gray-900 placeholder-gray-500 text-base leading-relaxed py-2"
              style={{
                minHeight: '24px',
                maxHeight: '120px',
                overflow: 'auto',
                fontFamily: 'inherit'
              }}
            />
          </div>
          
          <Button
            onClick={handleSendMessage}
            disabled={pendingResponse || message.trim().length === 0}
            size="sm"
            className={`
              w-10 h-10 p-0 rounded-xl transition-all duration-200 flex items-center justify-center
              ${message.trim().length > 0 && !pendingResponse 
                ? 'bg-black hover:bg-gray-800 text-white shadow-md hover:shadow-lg transform hover:scale-105' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {!pendingResponse ? (
              <Send className="w-4 h-4" />
            ) : (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            )}
          </Button>
        </div>
        
      </div>
    </div>
  );
}

const ModernChatDialog: React.FC = () => {
  const chatMessages = useModernChatStore((state) => state.chatMessages);
  const pendingResponse = useModernChatStore((state) => state.pendingResponse);

  return (
    <div className="flex flex-col h-full w-full relative">
      {/* Chat Messages Area - Takes full height minus input area */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {chatMessages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center">
                <div className="w-20 h-20 mb-6 opacity-80 animate-pulse">
                  <img 
                    src={BroccoliCharacter} 
                    alt="Broccoli Assistant" 
                    className="w-full h-full object-contain drop-shadow-lg"
                  />
                </div>
                                 <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  Welcome to LoanForeclosureWorkflow
                </h3>
                <p className="text-gray-600 text-lg max-w-md leading-relaxed">
                  I'm here to help you with your loan foreclosure queries.
                </p>
              </div>
            )}

            {chatMessages.map(({ id, message, msg_from }, index) => (
              <div
                key={id}
                className={`flex items-start gap-4 group ${
                  msg_from === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {/* Assistant Avatar */}
                {msg_from === 'gpt' && (
                  <div className="w-10 h-10 rounded-full border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0 overflow-hidden shadow-sm">
                    <img 
                      src={BroccoliCharacter} 
                      alt="Assistant" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Message Bubble */}
                <div
                  className={`
                    max-w-[75%] lg:max-w-[60%] px-5 py-3 rounded-2xl shadow-sm transition-all duration-200 group-hover:shadow-md
                    ${msg_from === 'user'
                      ? 'bg-black text-white rounded-br-md shadow-md'
                      : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                    }
                  `}
                >
                  <p className="text-base leading-relaxed whitespace-pre-wrap break-words">
                    {message}
                  </p>
                  
                  {/* Timestamp */}
                  <div className={`text-xs mt-2 ${msg_from === 'user' ? 'text-gray-300' : 'text-gray-400'}`}>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>

                {/* User Avatar */}
                {msg_from === 'user' && (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border-2 border-gray-300 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Typing Indicator */}
            {pendingResponse && (
              <div className="flex items-start gap-4 justify-start animate-fade-in">
                <div className="w-10 h-10 rounded-full border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0 overflow-hidden shadow-sm">
                  <img 
                    src={BroccoliCharacter} 
                    alt="Assistant" 
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="bg-white text-gray-900 rounded-2xl rounded-bl-md border border-gray-200 px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                          style={{
                            animationDelay: `${i * 0.2}s`,
                            animationDuration: '1.4s'
                          }}
                        />
                      ))}
                    </div>
                                         <span className="text-sm text-gray-500 italic">LoanForeclosureWorkflow is thinking...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom padding to ensure last message is visible above input */}
            <div className="h-24"></div>
          </div>
        </div>
      </div>

      {/* Input Area - Fixed at bottom */}
      <ModernInputArea />
    </div>
  );
};

export default ModernChatDialog; 